import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid, ReferenceLine, ReferenceArea,
  ComposedChart, Legend,
} from 'recharts';
import {
  Activity, Heart, Users, User, Loader2, TrendingUp, Clock,
} from 'lucide-react';
import AthleteMatchExposureSection from '../components/reports/AthleteMatchExposureSection';

const PERIODS = [
  { value: '7d', label: '7 Days' },
  { value: '14d', label: '14 Days' },
  { value: '28d', label: '28 Days' },
  { value: 'season', label: 'Season' },
];

const CHART_COLORS = {
  readiness: '#22c55e',
  fatigue: '#f43f5e',
  soreness: '#f59e0b',
  sleep: '#6366f1',
  load: '#22d3ee',
  rolling: '#f59e0b',
  distance_m: '#8b5cf6',
  shi_distance: '#ec4899',
  s7_distance: '#f97316',
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-xs shadow-lg">
      <div className="text-zinc-400 mb-1">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
          <span className="text-zinc-300">{p.name}: <span className="font-semibold text-zinc-100">{p.value != null ? p.value : '—'}</span></span>
        </div>
      ))}
    </div>
  );
};

const ContextMarkers = ({ markers, data }) => {
  if (!markers?.length || !data?.length) return null;
  const dateSet = new Set(data.map(d => d.date));
  return markers
    .filter(m => dateSet.has(m.date))
    .map((m, i) => (
      <ReferenceLine
        key={`marker-${i}`}
        x={m.date}
        stroke={m.type === 'match' ? '#ef4444' : '#38bdf8'}
        strokeDasharray="4 4"
        strokeWidth={1.5}
        label={{
          value: m.type === 'match' ? 'M' : 'R',
          position: 'top',
          fill: m.type === 'match' ? '#ef4444' : '#38bdf8',
          fontSize: 10,
          fontWeight: 700,
        }}
      />
    ));
};

export default function TrendChartsPage() {
  const { api } = useAuth();
  const { language } = useLanguage();
  const t = (en, it) => language === 'IT' ? it : en;

  const [mode, setMode] = useState('team');
  const [period, setPeriod] = useState('14d');
  const [teamId, setTeamId] = useState('all');
  const [athleteId, setAthleteId] = useState('');
  const [teams, setTeams] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [wellnessData, setWellnessData] = useState(null);
  const [loadData, setLoadData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch teams
  useEffect(() => {
    api.get('/teams').then(r => setTeams(r.data)).catch(() => {});
  }, [api]);

  // Fetch athletes for athlete mode
  useEffect(() => {
    if (mode === 'athlete') {
      const params = teamId !== 'all' ? `?team_id=${teamId}` : '';
      api.get(`/athletes${params}`).then(r => {
        setAthletes(r.data);
        if (r.data.length && !athleteId) setAthleteId(r.data[0].id);
      }).catch(() => {});
    }
  }, [api, mode, teamId, athleteId]);

  // Fetch trend data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ period });
      if (mode === 'team' && teamId !== 'all') params.set('team_id', teamId);
      if (mode === 'athlete' && athleteId) params.set('athlete_id', athleteId);

      const [wellRes, loadRes] = await Promise.all([
        api.get(`/trends/wellness?${params}`),
        api.get(`/trends/load?${params}`),
      ]);
      setWellnessData(wellRes.data);
      setLoadData(loadRes.data);
    } catch (err) {
      console.error('Failed to fetch trends:', err);
    } finally {
      setLoading(false);
    }
  }, [api, mode, period, teamId, athleteId]);

  useEffect(() => {
    if (mode === 'athlete' && !athleteId) return;
    fetchData();
  }, [fetchData, mode, athleteId]);

  const wp = wellnessData?.points || [];
  const lp = loadData?.points || [];
  const markers = loadData?.markers || [];

  const shortDate = (d) => {
    if (!d) return '';
    const parts = d.split('-');
    return `${parts[1]}/${parts[2]}`;
  };

  const wellnessFormatted = wp.map(p => ({ ...p, date: shortDate(p.date), _rawDate: p.date }));
  const loadFormatted = lp.map(p => ({ ...p, date: shortDate(p.date), _rawDate: p.date }));

  // Markers mapped to short date format
  const markersMapped = markers.map(m => ({ ...m, date: shortDate(m.date) }));

  // P0.5.15 — Map the shared TrendChartsPage period selector to an
  // absolute ISO date-range for the canonical Match Exposure endpoints.
  // The MX resolver denominator is period-scoped; the period selector
  // above is the single source of truth.
  const meDateRange = React.useMemo(() => {
    const to = new Date();
    const from = new Date();
    if (period === 'season') {
      // Football season (Aug → Jul).
      const seasonStart = to.getMonth() >= 7 ? to.getFullYear() : to.getFullYear() - 1;
      return {
        dateFrom: `${seasonStart}-08-01`,
        dateTo:   `${seasonStart + 1}-07-31`,
      };
    }
    const days = { '7d': 7, '14d': 14, '28d': 28 }[period] ?? 28;
    from.setDate(from.getDate() - days);
    return {
      dateFrom: from.toISOString().slice(0, 10),
      dateTo:   to.toISOString().slice(0, 10),
    };
  }, [period]);

  // Selected athlete's primary team (Match Exposure denominator).
  const selectedAthlete = athletes.find(a => a.id === athleteId);
  const meTeamId = selectedAthlete?.teams?.[0]?.team_id ||
                   selectedAthlete?.team_id || null;

  return (
    <div className="space-y-6 fade-in" data-testid="trend-charts-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-100 flex items-center gap-2">
            <TrendingUp className="w-6 h-6 text-amber-400" />
            {t('Monitoring Trends', 'Trend Monitoraggio')}
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            {t('Wellness & load data over time', 'Dati wellness e carico nel tempo')}
          </p>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          {/* Mode Toggle */}
          <div className="flex bg-zinc-900 border border-zinc-800 rounded-sm overflow-hidden" data-testid="mode-toggle">
            <button
              onClick={() => { setMode('team'); setAthleteId(''); }}
              className={`px-4 py-2 text-sm font-medium flex items-center gap-1.5 transition-colors ${
                mode === 'team' ? 'bg-amber-500/15 text-amber-400 border-r border-amber-500/30' : 'text-zinc-400 hover:text-zinc-200 border-r border-zinc-800'
              }`}
              data-testid="mode-team-btn"
            >
              <Users className="w-3.5 h-3.5" /> {t('Team', 'Squadra')}
            </button>
            <button
              onClick={() => setMode('athlete')}
              className={`px-4 py-2 text-sm font-medium flex items-center gap-1.5 transition-colors ${
                mode === 'athlete' ? 'bg-amber-500/15 text-amber-400' : 'text-zinc-400 hover:text-zinc-200'
              }`}
              data-testid="mode-athlete-btn"
            >
              <User className="w-3.5 h-3.5" /> {t('Athlete', 'Atleta')}
            </button>
          </div>

          {/* Team Filter */}
          <Select value={teamId} onValueChange={(v) => { setTeamId(v); setAthleteId(''); }}>
            <SelectTrigger className="w-[140px] bg-zinc-950 border-zinc-800" data-testid="team-filter">
              <SelectValue placeholder="Team" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800">
              <SelectItem value="all">{t('All Teams', 'Tutti')}</SelectItem>
              {teams.map(tm => (
                <SelectItem key={tm.id} value={tm.id}>{tm.code}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Athlete Selector */}
          {mode === 'athlete' && (
            <Select value={athleteId} onValueChange={setAthleteId}>
              <SelectTrigger className="w-[200px] bg-zinc-950 border-zinc-800" data-testid="athlete-selector">
                <SelectValue placeholder={t('Select Athlete', 'Seleziona Atleta')} />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                {athletes.map(a => (
                  <SelectItem key={a.id} value={a.id}>{a.first_name} {a.last_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {/* Period Filter */}
          <div className="flex bg-zinc-900 border border-zinc-800 rounded-sm overflow-hidden" data-testid="period-filter">
            {PERIODS.map(p => (
              <button
                key={p.value}
                onClick={() => setPeriod(p.value)}
                className={`px-3 py-2 text-xs font-medium transition-colors ${
                  period === p.value ? 'bg-zinc-700 text-zinc-100' : 'text-zinc-500 hover:text-zinc-300'
                }`}
                data-testid={`period-${p.value}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-6 h-6 animate-spin text-zinc-500" />
        </div>
      ) : (
        <>
          {/* WELLNESS SECTION */}
          <div data-testid="wellness-trends-section">
            <h2 className="text-lg font-semibold text-zinc-100 flex items-center gap-2 mb-4">
              <Heart className="w-5 h-5 text-rose-400" />
              {t('Wellness Trends', 'Trend Wellness')}
              {mode === 'team' && <span className="text-xs text-zinc-500 font-normal ml-2">{t('Team Average', 'Media Squadra')}</span>}
            </h2>

            {wp.length === 0 ? (
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardContent className="py-8 text-center text-zinc-500 text-sm">
                  {t('No wellness data for this period.', 'Nessun dato wellness per questo periodo.')}
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Readiness Score */}
                <Card className="bg-zinc-900/50 border-zinc-800" data-testid="chart-readiness">
                  <CardHeader className="pb-1">
                    <CardTitle className="text-sm text-zinc-400">{t('Readiness Score', 'Punteggio Readiness')}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={180}>
                      <ComposedChart data={wellnessFormatted}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                        <ReferenceArea y1={60} y2={100} fill="#22c55e" fillOpacity={0.05} />
                        <ReferenceArea y1={40} y2={60} fill="#f59e0b" fillOpacity={0.05} />
                        <ReferenceArea y1={0} y2={40} fill="#ef4444" fillOpacity={0.05} />
                        <XAxis dataKey="date" tick={{ fill: '#71717a', fontSize: 10 }} />
                        <YAxis domain={[0, 100]} tick={{ fill: '#71717a', fontSize: 10 }} />
                        <Tooltip content={<CustomTooltip />} />
                        <Line type="monotone" dataKey="readiness" stroke={CHART_COLORS.readiness} strokeWidth={2} dot={{ fill: CHART_COLORS.readiness, r: 3 }} name={t('Readiness', 'Readiness')} connectNulls />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Fatigue */}
                <Card className="bg-zinc-900/50 border-zinc-800" data-testid="chart-fatigue">
                  <CardHeader className="pb-1">
                    <CardTitle className="text-sm text-zinc-400">{t('Fatigue', 'Fatica')} <span className="text-[10px] text-zinc-600">(1=low, 5=high)</span></CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={180}>
                      <ComposedChart data={wellnessFormatted}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                        <ReferenceArea y1={1} y2={2} fill="#22c55e" fillOpacity={0.06} />
                        <ReferenceArea y1={3} y2={4} fill="#f59e0b" fillOpacity={0.06} />
                        <ReferenceArea y1={4} y2={5} fill="#ef4444" fillOpacity={0.06} />
                        <XAxis dataKey="date" tick={{ fill: '#71717a', fontSize: 10 }} />
                        <YAxis domain={[1, 5]} tick={{ fill: '#71717a', fontSize: 10 }} />
                        <Tooltip content={<CustomTooltip />} />
                        <Line type="monotone" dataKey="fatigue" stroke={CHART_COLORS.fatigue} strokeWidth={2} dot={{ fill: CHART_COLORS.fatigue, r: 3 }} name={t('Fatigue', 'Fatica')} connectNulls />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Soreness */}
                <Card className="bg-zinc-900/50 border-zinc-800" data-testid="chart-soreness">
                  <CardHeader className="pb-1">
                    <CardTitle className="text-sm text-zinc-400">{t('Soreness', 'Dolori')} <span className="text-[10px] text-zinc-600">(1=low, 5=high)</span></CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={180}>
                      <ComposedChart data={wellnessFormatted}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                        <ReferenceArea y1={1} y2={2} fill="#22c55e" fillOpacity={0.06} />
                        <ReferenceArea y1={3} y2={4} fill="#f59e0b" fillOpacity={0.06} />
                        <ReferenceArea y1={4} y2={5} fill="#ef4444" fillOpacity={0.06} />
                        <XAxis dataKey="date" tick={{ fill: '#71717a', fontSize: 10 }} />
                        <YAxis domain={[1, 5]} tick={{ fill: '#71717a', fontSize: 10 }} />
                        <Tooltip content={<CustomTooltip />} />
                        <Line type="monotone" dataKey="soreness" stroke={CHART_COLORS.soreness} strokeWidth={2} dot={{ fill: CHART_COLORS.soreness, r: 3 }} name={t('Soreness', 'Dolori')} connectNulls />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Sleep */}
                <Card className="bg-zinc-900/50 border-zinc-800" data-testid="chart-sleep">
                  <CardHeader className="pb-1">
                    <CardTitle className="text-sm text-zinc-400">{t('Sleep Quality', 'Qualità Sonno')} <span className="text-[10px] text-zinc-600">(1=poor, 5=excellent)</span></CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={180}>
                      <ComposedChart data={wellnessFormatted}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                        <ReferenceArea y1={4} y2={5} fill="#22c55e" fillOpacity={0.06} />
                        <ReferenceArea y1={2} y2={3} fill="#f59e0b" fillOpacity={0.06} />
                        <ReferenceArea y1={1} y2={2} fill="#ef4444" fillOpacity={0.06} />
                        <XAxis dataKey="date" tick={{ fill: '#71717a', fontSize: 10 }} />
                        <YAxis domain={[1, 5]} tick={{ fill: '#71717a', fontSize: 10 }} />
                        <Tooltip content={<CustomTooltip />} />
                        <Line type="monotone" dataKey="sleep" stroke={CHART_COLORS.sleep} strokeWidth={2} dot={{ fill: CHART_COLORS.sleep, r: 3 }} name={t('Sleep', 'Sonno')} connectNulls />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>

          {/* LOAD SECTION */}
          <div data-testid="load-trends-section">
            <h2 className="text-lg font-semibold text-zinc-100 flex items-center gap-2 mb-4">
              <Activity className="w-5 h-5 text-cyan-400" />
              {t('Load / RPE Trends', 'Trend Carico / RPE')}
              {mode === 'team' && <span className="text-xs text-zinc-500 font-normal ml-2">{t('Team Average', 'Media Squadra')}</span>}
            </h2>

            {/* Context markers legend */}
            {markers.length > 0 && (
              <div className="flex items-center gap-4 mb-3 text-xs text-zinc-500" data-testid="markers-legend">
                <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-red-500 inline-block" /> {t('Match Day', 'Partita')}</span>
                <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-sky-400 inline-block" /> {t('Recovery', 'Recupero')}</span>
              </div>
            )}

            {lp.length === 0 ? (
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardContent className="py-8 text-center text-zinc-500 text-sm">
                  {t('No load data for this period.', 'Nessun dato carico per questo periodo.')}
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* sRPE Load + Rolling 7d */}
                <Card className="bg-zinc-900/50 border-zinc-800" data-testid="chart-load">
                  <CardHeader className="pb-1">
                    <CardTitle className="text-sm text-zinc-400">{t('sRPE Load', 'Carico sRPE')}
                      <span className="text-[10px] text-amber-400/60 ml-2">{t('line = rolling 7d', 'linea = 7g mobile')}</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={200}>
                      <ComposedChart data={loadFormatted}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                        {ContextMarkers({ markers: markersMapped, data: loadFormatted })}
                        <XAxis dataKey="date" tick={{ fill: '#71717a', fontSize: 10 }} />
                        <YAxis tick={{ fill: '#71717a', fontSize: 10 }} />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="load" fill={CHART_COLORS.load} fillOpacity={0.7} radius={[2, 2, 0, 0]} name={t('Daily Load', 'Carico')} />
                        <Line type="monotone" dataKey="rolling_7d" stroke={CHART_COLORS.rolling} strokeWidth={2} dot={false} name={t('7d Rolling', '7g Mobile')} connectNulls />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Total Distance */}
                <Card className="bg-zinc-900/50 border-zinc-800" data-testid="chart-distance">
                  <CardHeader className="pb-1">
                    <CardTitle className="text-sm text-zinc-400">{t('Total Distance', 'Distanza Totale')} <span className="text-[10px] text-zinc-600">(m)</span></CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={200}>
                      <ComposedChart data={loadFormatted}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                        {ContextMarkers({ markers: markersMapped, data: loadFormatted })}
                        <XAxis dataKey="date" tick={{ fill: '#71717a', fontSize: 10 }} />
                        <YAxis tick={{ fill: '#71717a', fontSize: 10 }} />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="distance_m" fill={CHART_COLORS.distance_m} fillOpacity={0.7} radius={[2, 2, 0, 0]} name={t('Distance (m)', 'Distanza (m)')} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* SHI Distance (>16 km/h) */}
                <Card className="bg-zinc-900/50 border-zinc-800" data-testid="chart-shi-distance">
                  <CardHeader className="pb-1">
                    <CardTitle className="text-sm text-zinc-400">{t('SHI Distance', 'Distanza SHI')} <span className="text-[10px] text-zinc-600">({'>'}16 km/h)</span></CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={200}>
                      <ComposedChart data={loadFormatted}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                        {ContextMarkers({ markers: markersMapped, data: loadFormatted })}
                        <XAxis dataKey="date" tick={{ fill: '#71717a', fontSize: 10 }} />
                        <YAxis tick={{ fill: '#71717a', fontSize: 10 }} />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="shi_distance" fill={CHART_COLORS.shi_distance} fillOpacity={0.7} radius={[2, 2, 0, 0]} name={t('SHI (m)', 'SHI (m)')} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* S7 Distance (>25 km/h) */}
                <Card className="bg-zinc-900/50 border-zinc-800" data-testid="chart-s7-distance">
                  <CardHeader className="pb-1">
                    <CardTitle className="text-sm text-zinc-400">{t('Sprint Distance', 'Distanza Sprint')} <span className="text-[10px] text-zinc-600">({'>'}25 km/h)</span></CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={200}>
                      <ComposedChart data={loadFormatted}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                        {ContextMarkers({ markers: markersMapped, data: loadFormatted })}
                        <XAxis dataKey="date" tick={{ fill: '#71717a', fontSize: 10 }} />
                        <YAxis tick={{ fill: '#71717a', fontSize: 10 }} />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="s7_distance" fill={CHART_COLORS.s7_distance} fillOpacity={0.7} radius={[2, 2, 0, 0]} name={t('Sprint (m)', 'Sprint (m)')} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>

          {/* P0.5.15 — MATCH EXPOSURE TRENDS (athlete mode only) */}
          {mode === 'athlete' && athleteId && (
            <div data-testid="match-exposure-trends-section">
              <h2 className="text-lg font-semibold text-zinc-100 flex items-center gap-2 mb-4">
                <Clock className="w-5 h-5 text-cyan-400" />
                {t('Match Exposure Trends', 'Trend Match Exposure')}
                <span className="text-xs text-zinc-500 font-normal ml-2">
                  {t('Canonical, backend-derived', 'Canonico, da backend')}
                </span>
              </h2>
              <AthleteMatchExposureSection
                api={api}
                athleteId={athleteId}
                teamId={meTeamId}
                dateFrom={meDateRange.dateFrom}
                dateTo={meDateRange.dateTo}
                t={t}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}
